/**
 * Patient consent — one combined acknowledgement, one box.
 *
 * The patient ticks ONE box during onboarding to grant every constituent
 * consent below, including the de-identified secondary use. v3 corrects
 * "information manager" to "agent" (NS PHIA ss. 28–29), generalizes the
 * custodian to the patient's own health-care provider (Model A referrals),
 * and discloses the Canadian cloud relay. v4 flips the optional checkbox from
 * decline-polarity to AGREE-polarity: checked = express opt-in. The v3 decline
 * box sat right under the main acknowledgement and patients in
 * checkbox-completion momentum ticked it accidentally, recording declines they
 * did not intend (case imp_36fc9299, Michael 2026-09-02).
 *
 * KEEP IN SYNC:
 *   - CONSENT_VERSION must match CURRENT_CONSENT_VERSION in
 *     med_form_app/backend/consent_gate.py
 *   - PATIENT_CONSENT_ITEMS[].key + ANALYTICS_SECONDARY_USE_KEY must match
 *     PATIENT_CONSENTS in med_form_app/backend/consent_service.py
 *   - The displayed terms must match docs/pilot/CONSENT_FORM_V7.md
 *
 * v5 (2026-10-11) adds point 10 — the fee policy and the payment processor —
 * as CONSENT_FEES_DISCLOSURE, shown in the consent card. Informational, not a
 * constituent consent key. A v4 patient is not re-prompted (the gate checks
 * age only), so the per-form fee notice repeats the processor disclosure.
 *
 * v7 (2026-10-18, Michael's ruling) folds the de-identified secondary use
 * INTO the single acknowledgement and removes the separate optional box and
 * its opt-out: there is one box and no choice. Because the choice is gone,
 * the bundled point names the commercial use outright — analyses sold as paid
 * products to researchers, insurers and pharmaceutical companies — rather
 * than leaving it to the optional box's small print. The withdrawal sentence
 * is retired with the box. `analytics_secondary_use` stays a machine key (the
 * research-database partition reads it) and is granted by the one box; it
 * stays in the backend's OPTIONAL_CONSENTS so a v4-v6 patient who declined is
 * never blocked from care. v7 also cuts the ownership disclosure to two
 * sentences (Michael: not elaborated on too much).
 *
 * v6 (2026-10-16) rewords point 10's fee-policy sentence to Michael's
 * approved text: the fee is set by the physician's practice or by Parseo
 * Health, it is for preparing and sending the form, and the patient can
 * decline before the form is sent without being charged. v5's "a fee can
 * always be waived if you cannot pay" is retired (ruling 2026-10-14, no free
 * path). The processor sentence is unchanged. A v5 patient is not re-prompted
 * either; the per-form fee notice carries the decline and cancellation terms.
 */

// Source of truth for which version of the terms the patient saw. Bump this
// (and the backend constant + CONSENT_FORM doc) whenever the wording changes.
export const CONSENT_VERSION = "v7-2026-10-18";

// v3 ownership disclosure (CONSENT_FORM_V3.md "About Parseo Health").
export const CONSENT_OWNERSHIP_DISCLOSURE =
  "Parseo Health Inc. is owned by Dr. Michael Mindrum, a Nova Scotia physician. Your " +
  "own health-care provider reviews, approves, and signs your form and remains " +
  "responsible for its contents.";

// The constituent consents bundled into the single acknowledgement. Keys are
// the canonical machine taxonomy shared with the backend; title/detail are the
// patient-facing copy rendered inside the one consent card.
export const PATIENT_CONSENT_ITEMS = [
  {
    key: "tos",
    title: "Terms of Service",
    detail:
      "I agree to use Parseo Health to manage my health forms and prior authorizations.",
  },
  {
    key: "phi_collection",
    title: "Collection and use of my health information",
    detail:
      "My personal health information (name, health card number, conditions, medications) is collected to complete prior authorization forms on my behalf, and is stored securely in Canada. Information I submit online passes through Parseo Health's secure Canadian relay (Montréal), where it is encrypted in transit and deleted once received.",
  },
  {
    key: "ai_processing",
    title: "AI-assisted processing",
    detail:
      "Clinical information — with my personal identifiers removed first — may be processed by AI to help extract and organize data for my forms.",
  },
  {
    key: "deidentified_analytics",
    title: "Use of my de-identified information",
    detail:
      "Information about my care, with my personal identifiers removed (no name, date of birth, health-card number, or other identifiers), may be used to measure and improve how Parseo Health works, and may be combined with other patients' de-identified information in analyses that Parseo Health Inc. provides as paid products or services to third parties such as researchers, insurers, and pharmaceutical companies. No analysis is ever based on my individual record alone, this information cannot be traced back to me, and it is never used to make decisions about my care or my coverage.",
  },
  {
    key: "third_party_disclosure",
    title: "Sharing with my care team",
    detail:
      "Completed forms may be shared with my physician, insurer, and pharmacy as needed for my prior authorization. Only the minimum necessary information is shared with each party. My own health-care provider reviews, approves, and signs the final form and remains responsible for its contents. If my insurance plan is in another person's name, that person may be asked to sign their section of the form; they see only the plan and signature portions, never my clinical information.",
  },
];

// The de-identified use is ONE bundled point in the single acknowledgement
// (v7, 2026-10-18). Two machine keys still travel with every record because
// the backend partitions the research database on `analytics_secondary_use`
// (consent_service.ANALYTICS_CONSENT_KEY) — the one box grants both. There is
// no separate box and no opt-out: Michael's ruling of 2026-10-18, taken with
// the commercial use named explicitly in the point above rather than implied.
export const ANALYTICS_SECONDARY_USE_KEY = "analytics_secondary_use";

// v6 point 10 (CONSENT_FORM_V6.md): fees for forms and the payment processor.
// The fee-policy sentences are Michael's approved text of 2026-10-16,
// verbatim; the processor sentence is v5's, unchanged (PATIENT_PAYMENT_PLAN.md
// §6).
export const CONSENT_FEES_DISCLOSURE = {
  title: "Fees for forms, and how a fee is paid",
  detail:
    "Some forms carry a fee, set by your physician's practice or by Parseo Health. The " +
    "fee is for the work of preparing and sending the form, not for the insurer's " +
    "decision. You can decline before the form is sent, and nothing is charged. If a fee " +
    "applies to a form, your card details are handled by Stripe Payments Canada, Ltd., " +
    "which processes card payments in the United States; Parseo keeps no card numbers.",
};

// The single statement shown beside the one checkbox.
export const CONSENT_ACKNOWLEDGEMENT =
  "I have read and understood the points above, and I consent to Parseo Health collecting and using my health information to prepare my prior-authorization forms as described.";

/**
 * Build the consent record persisted with a submission / patient profile.
 *
 * Shape mirrors what record_patient_consent (routers/patient.py) stores in the
 * profile `consent` block and what consent_gate.check_consent reads:
 *
 *   {
 *     method: "online_acknowledgement",
 *     version: "v6-2026-10-16",
 *     signed_at: "<ISO-8601>",
 *     consents: { tos: true, phi_collection: true, ai_processing: true,
 *                 deidentified_analytics: true, third_party_disclosure: true,
 *                 analytics_secondary_use: true|false }
 *   }
 *
 * analytics_secondary_use is EXPLICIT in every record and, since v7, follows
 * the single acknowledgement: accepted => true, anything else => false. The
 * backend gate is default-deny, so false stays the safe value for a record
 * that was never accepted.
 *
 * @param {object}  opts
 * @param {boolean} opts.accepted   whether the patient ticked the single box
 * @param {string}  [opts.signedAt] ISO timestamp; defaults to now
 */
export function buildConsentRecord(
  { accepted, signedAt }: { accepted?: boolean; signedAt?: string } = {}
) {
  const granted = accepted === true;
  const consents = PATIENT_CONSENT_ITEMS.reduce((acc: Record<string, boolean>, item) => {
    acc[item.key] = granted;
    return acc;
  }, {} as Record<string, boolean>);
  // Explicit true/false, never missing. v7: the de-identified point is inside
  // the single acknowledgement, so the one box grants this key too.
  consents[ANALYTICS_SECONDARY_USE_KEY] = granted;
  return {
    method: "online_acknowledgement",
    version: CONSENT_VERSION,
    signed_at: signedAt || new Date().toISOString(),
    consents,
  };
}
